/**
 * Hub Partner API client (T06).
 *
 * Wraps the two read-only endpoints exposed by the hub's PartnerController
 * (T11, services/hub/src/modules/partner/partner.controller.ts):
 *
 *   GET /api/partner/me           — referral code + KPIs (PartnerSummary)
 *   GET /api/partner/referrals    — paginated list of the user's referrals
 *
 * Both endpoints require an authenticated session; the shared `request`
 * helper already sends `credentials: 'include'`.
 *
 * Numeric money fields (`total_earned_usd`, `pending_payout_usd`,
 * `earned_usd`) arrive as decimal **strings** (PostgreSQL NUMERIC(20,8)) and
 * are kept as strings end-to-end to avoid float drift — convert via
 * `Number(value)` only at the very last formatting step.
 */
import { request } from './client';

export interface PartnerSummary {
  /** Stable short code, e.g. `gnk-7f3a9b21`. */
  referral_code: string;
  total_referrals: number;
  active_referrals: number;
  /** Decimal string, 8 fractional digits. */
  total_earned_usd: string;
  /** MVP: equals total_earned_usd until a payout flow exists. */
  pending_payout_usd: string;
}

export interface ReferralRow {
  /** Shortened referee user id (server-shortened, do not re-trim). */
  referee_user_id_short: string;
  /** ISO timestamp — when the referee signed up. */
  referred_at: string;
  /** ISO timestamp of the first `charge` ledger entry, or null. */
  first_charge_at: string | null;
  /** Decimal string, 8 fractional digits. */
  earned_usd: string;
}

export interface ListReferralsResponse {
  rows: ReferralRow[];
  total: number;
}

export interface ListReferralsParams {
  page?: number;
  page_size?: number;
}

export const partnerApi = {
  me: (): Promise<PartnerSummary> => request<PartnerSummary>('/api/partner/me'),

  referrals: (params: ListReferralsParams = {}): Promise<ListReferralsResponse> => {
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.page_size != null) q.set('page_size', String(params.page_size));
    const qs = q.toString();
    return request<ListReferralsResponse>(`/api/partner/referrals${qs ? `?${qs}` : ''}`);
  },
};
